import {Observable, Subject} from 'rxjs';
import {DynamicInputsInterface} from './DynamicInputs.interface';

/**
 * Represents a loaded file. This class contains the file data and additional information.
 */
export class ExcFileItemQuestionnaire {

  /**
   * Returns the size in a human readable format like 0.7MB
   */
  get friendlyFileSize(): string | undefined {
    if (!this._friendlyFileSize) {
      let i = -1;
      let tmpFileSize = this.size;
      const byteUnits = [' KB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
      do {
        if (tmpFileSize) {
          tmpFileSize = tmpFileSize / 1024;
        }
        i++;
      } while (tmpFileSize && tmpFileSize > 1024);
      if (tmpFileSize) {
        this._friendlyFileSize = Math.max(tmpFileSize, 0.1).toFixed(1) + byteUnits[i];
      }
    }
    return this._friendlyFileSize;
  }

  /**
   * Returns the created at timestamp as ISO String (without time zone consideration)
   */
  get createdAtString(): string {
    return new Date(this.createdAt as number).toISOString();
  }

  /**
   * Returns this instance as blob
   */
  get asBlob(): Blob {
    return new Blob([this.data as ArrayBuffer], {type: this.type});
  }

  private _friendlyFileSize: string | undefined;

  /**
   * A flag that determines if the data of this ExcFileItem is still loading with a FileReader
   */
  public isLoading = false;

  /**
   * Misc data than can be anything ... But per default it is label and description
   */
  public misc: { [key: string]: any; } | undefined;

  public id: string | undefined;
  public fkid: string | undefined;
  /**
   * Update token
   */
  public upt: string | undefined;
  public owner: string | undefined;
  /**
   * A timestamp from when the data was uploaded in milliseconds
   */
  public createdAt: number | undefined;
  public fullName: string | undefined;
  public name: string | undefined;
  public extension: string | undefined;
  public type: string | undefined;
  public size: number | undefined;
  public data: ArrayBuffer | undefined;
  private _afterLoading: Subject<ExcFileItemQuestionnaire> = new Subject<ExcFileItemQuestionnaire>();
  public afterLoading: Observable<ExcFileItemQuestionnaire> = this._afterLoading.asObservable();

  /**
   * Creates as ExcFileItem object from an api response object. The dynamicInputs array defines which data
   * from api response data should be saved in misc
   * @param response - data object that provides the source data
   * @param dynamicInputs - data keys for misc data
   */
  public static fromApiResponse(response: any, dynamicInputs: DynamicInputsInterface[]): ExcFileItemQuestionnaire {
    const fileItem = new ExcFileItemQuestionnaire();
    fileItem.id = response.ID;
    fileItem.fkid = response.FKID;
    fileItem.upt = response.UPT;
    fileItem.owner = response.CRU;
    fileItem.createdAt = response.CRD;

    fileItem.fullName = response.FileName;
    const nameParts = fileItem?.fullName?.split('.');
    fileItem.extension = nameParts?.pop();
    fileItem.name = nameParts?.join('.');
    fileItem.type = response.MIME;
    fileItem.size = response.Size;

    fileItem.misc = {};
    // handle misc data
    // @ts-ignore
    dynamicInputs.forEach(item => fileItem.misc[item.apiValue] = response[item.apiValue]);
    return fileItem;
  }

  /**
   * Concerts an ExcFileItem to an api response object
   * param excFileItem
   */
  // TODO
  //  bad design decision,.the EXCFileItem should have the same property like the API response ...
  //  the converting is bad..
  public static toApiResponse(excFileItem: ExcFileItemQuestionnaire, dynamicInputs: DynamicInputsInterface[]): any {
    const response: any = {ID: undefined, FKID: undefined, UPT: undefined, CRU: undefined,
      CRD: undefined, FileName: undefined, MIME: undefined, Size: undefined};
    response.ID = excFileItem.id;
    response.FKID = excFileItem.fkid;
    response.UPT = excFileItem.upt;
    response.CRU = excFileItem.owner;
    response.CRD = excFileItem.createdAt;
    response.FileName = excFileItem.fullName;
    response.MIME = excFileItem.type;
    response.Size = excFileItem.size;

    // handle misc data
    // @ts-ignore
    dynamicInputs.forEach(item => response[item.apiValue] = excFileItem.misc[item.apiValue]);
    return response;
  }

  /**
   * Creates an ExcFileItem instance from a file
   * @param file - file that provides the source data
   * @param misc - data that should saved as misc data
   */
  public static fromFile(file: File, misc ?: any): ExcFileItemQuestionnaire {
    const fileItem = new ExcFileItemQuestionnaire();
    fileItem.isLoading = true;

    // set meta information
    fileItem.fullName = file.name;
    const nameParts = fileItem.fullName.split('.');
    fileItem.extension = nameParts.pop();
    fileItem.name = nameParts.join('.');
    fileItem.size = file.size;
    fileItem.type = file.type;

    // load file data
    const fileReader = new FileReader();
    fileReader.onload = () => {
      fileItem.data = fileReader.result as ArrayBuffer;
      fileItem.isLoading = false;
      fileItem._afterLoading.next(fileItem);
      fileItem._afterLoading.complete();
    };
    fileReader.readAsArrayBuffer(file);

    // set misc data
    fileItem.misc = misc;

    return fileItem;
  }

  /**
   * Creates a FormData object from an array of ExcFileItems. Consider that only the misc properties of the
   * first ExcFileItems are used, whereas the blob value is used from each ExcFileItem.
   * param excFileItems
   */
  static toFormData(excFileItems: ExcFileItemQuestionnaire[]): FormData {
    const formData = new FormData();
    // set all misc values.
    // @ts-ignore
    Object.keys(excFileItems[0].misc as object).forEach(key => formData.append(key, excFileItems[0].misc[key]));
    // set file values
    excFileItems.forEach(item => formData.append('file[]', item.asBlob, item.fullName));
    return formData;
  }

  /**
   * Returns a deep copy of this instance
   */
  getCopy(): ExcFileItemQuestionnaire {
    const copy = new ExcFileItemQuestionnaire();
    // copy all properties
    Object.keys(this).forEach(key => (copy as any)[key] = (this as any)[key]);
    // make a deep copy of data
    if (this.data) {
      copy.data = this.data.slice(0);
    }
    // set some vars to default value
    copy.isLoading = false;
    return copy;
  }
}
